import type { User } from '@features/auth/types';
import { splitDisplayName } from '@shared/utils/initials';

/** Display names that are placeholders, not real given names. */
const PLACEHOLDER_NAMES = new Set(['you', 'your', 'user', 'name', 'guest', 'your name']);

/**
 * Ensures `firstName`, `lastName`, and `name` are consistent for initials and UI.
 * Backfills structured names from `name` and vice versa.
 */
export function normalizeAuthUser(user: User): User {
  let firstName = user.firstName.trim();
  let lastName = user.lastName.trim();
  let name = user.name.trim();

  if (!firstName && !lastName && name) {
    const split = splitDisplayName(name);
    firstName = split.firstName;
    lastName = split.lastName;
    name = split.displayName;
  }

  if (!name && (firstName || lastName)) {
    name = [firstName, lastName].filter(Boolean).join(' ');
  }

  const lowerName = name.toLowerCase();
  if (name && PLACEHOLDER_NAMES.has(lowerName) && user.email) {
    const local = user.email.split('@')[0]?.replace(/[._-]+/g, ' ').trim() ?? '';
    if (local) {
      const fromEmail = splitDisplayName(local);
      if (fromEmail.firstName && !PLACEHOLDER_NAMES.has(fromEmail.firstName.toLowerCase())) {
        firstName = fromEmail.firstName;
        lastName = fromEmail.lastName;
        name = fromEmail.displayName;
      }
    }
  }

  return { ...user, name, firstName, lastName };
}
